/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Directive, AfterViewInit, OnDestroy, Optional, Injector, NgZone, Input } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { Subject } from 'rxjs';
import { takeUntil, take, filter, tap } from 'rxjs/operators';
import { isBackKey } from 'utils/lib/web-events/keyboard-event';
import { NvGenericDialogData } from '../components/nv-generic-dialog/nv-generic-dialog.types';

@Directive({
    selector: '[nvBackToCloseModal]',
    standalone: true
})
export class BackToCloseModalDirective implements AfterViewInit, OnDestroy {
    @Input() backToCloseResult: any;
    private destroy$ = new Subject<boolean>();

    constructor(
        @Optional() private dialogRef: MatDialogRef<any>,
        @Optional() private bottomSheetRef: MatBottomSheetRef<any>,
        private injector: Injector,
        private ngZone: NgZone
    ) {}

    ngAfterViewInit() {
        const configData = this.dialogRef
            ? this.injector.get<NvGenericDialogData>(MAT_DIALOG_DATA, null)
            : this.bottomSheetRef
              ? this.injector.get<NvGenericDialogData>(MAT_BOTTOM_SHEET_DATA, null)
              : undefined;
        const reference = this.dialogRef ?? this.bottomSheetRef;
        if (!reference || configData?.disableBackToClose) {
            return;
        }

        // Get key event from overlayRef.keydownEvents of dialog/bottom-sheet,
        // to make sure key event can be received even though focus is on mat-dialog-container
        this.ngZone.runOutsideAngular(() => {
            reference
                .keydownEvents()
                .pipe(
                    filter(isBackKey),
                    tap(event => {
                        event.stopPropagation();
                        event.preventDefault();
                    }),
                    // Keep listening while Back is blocked, including repeated presses.
                    filter(() => !configData?.consumeBackWithoutClosing),
                    take(1),
                    takeUntil(this.destroy$)
                )
                .subscribe(event => this.close(event));
        });
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    private close(event: Event) {
        const result =
            (typeof this.backToCloseResult === 'function' ? this.backToCloseResult() : this.backToCloseResult) ?? event;
        this.dialogRef?.close(result);
        this.bottomSheetRef?.dismiss(result);
    }
}
